import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, AlertTriangle, ArrowLeft, Check, CheckCircle, ChevronRight,
  ClipboardCheck, Crosshair, Eye, HelpCircle, Image,
  Loader2, Play, Server, Shield, Target, XCircle
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer,
  ScatterChart, Scatter, CartesianGrid, Cell, Legend, ReferenceLine
} from 'recharts';
import fetchWithTenant from '../utils/fetchWithTenant';
import RaLicenseBanner from '../components/RaLicenseBanner';
import '../styles/v2/pages/recording-assurance.css';

const VERDICT_CONFIG = {
  ACCURATE_POSITIVE:    { color: '#22c55e', bg: 'rgba(34, 197, 94, 0.08)', label: 'Confirmed recording', icon: CheckCircle },
  ACCURATE_NEGATIVE:    { color: '#22c55e', bg: 'rgba(34, 197, 94, 0.08)', label: 'Correctly detected failure', icon: CheckCircle },
  FALSE_POSITIVE:       { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.08)', label: 'False alarm (camera is actually OK)', icon: XCircle },
  FALSE_NEGATIVE:       { color: '#dc2626', bg: 'rgba(220, 38, 38, 0.08)', label: 'Missed failure (camera has issues)', icon: AlertTriangle },
  EXPECTED_LOW_COVERAGE:{ color: '#64748b', bg: 'rgba(100, 116, 139, 0.08)', label: 'Normal (motion/schedule camera)', icon: Check },
  INCONCLUSIVE:         { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.08)', label: 'Needs more data', icon: HelpCircle },
  ERROR:                { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.08)', label: 'Check failed', icon: AlertTriangle },
};

const STORAGE_KEYS = {
  audit: 'ra_accuracy_audit',
  calibration: 'ra_accuracy_calibration',
  history: 'ra_accuracy_history',
  tab: 'ra_accuracy_tab',
};

function saveResult(key, data) {
  try { sessionStorage.setItem(key, JSON.stringify(data)); } catch (e) { /* full */ }
}

function loadResult(key) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) { return null; }
}

const Tooltip = ({ text }) => {
  const [show, setShow] = useState(false);
  return (
    <span className="ra-tooltip-wrapper" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <HelpCircle size={14} className="ra-tooltip-icon" />
      {show && <div className="ra-tooltip-popup">{text}</div>}
    </span>
  );
};

/* ── Deviation Root Cause Analysis ── */
function getDeviationAnalysis(point) {
  const dev = point.deviation || 0;
  const mode = point.recordingMode;
  const actual = point.actualCoverage ?? 0;
  const reported = point.raConfidence ?? 0;
  const bl = point.baseline;
  const method = point.calibrationMethod;

  if (dev < 15) return null;

  // Baseline-calibrated motion camera with anomaly
  if (method === 'baseline' && bl && bl.mean !== null) {
    const range = bl.expectedRange || { low: 0, high: 100 };
    if (actual < range.low) {
      return {
        cause: 'Coverage below baseline for this time window',
        detail: 'This camera normally averages ' + bl.mean + '% coverage at this time of week (based on ' + bl.samples + ' samples). Current coverage is ' + actual + '%, which is below the expected range of ' + range.low + '%-' + range.high + '%. This could mean reduced activity, a shifted camera view, or a motion detection issue.',
        action: 'Check the camera\'s live view and motion detection settings. If the environment has changed (new furniture, lighting), the motion sensor may need recalibration. If this is a one-time event, the baseline will absorb it naturally.',
        severity: actual < range.low - 20 ? 'high' : 'medium'
      };
    }
    if (actual > range.high) {
      return {
        cause: 'Coverage above baseline for this time window',
        detail: 'This camera normally averages ' + bl.mean + '% coverage at this time of week but is currently at ' + actual + '%. Higher-than-expected activity could mean unusual foot traffic, an event, or the camera\'s motion sensitivity was increased.',
        action: 'This is generally not a concern unless unexpected. If the camera was recently adjusted, the baseline will update over the next few check cycles.',
        severity: 'low'
      };
    }
  }

  if (reported > actual) {
    if (mode === 'MOTION' || mode === 'SCHEDULED') {
      return {
        cause: 'Motion/schedule camera with variable coverage',
        detail: 'This camera records on motion or schedule. Coverage naturally fluctuates, and RA may be averaging over a longer baseline than the current check window.',
        action: 'No action needed. Consider switching RA to motion-aware mode for this camera so it expects variable coverage.',
        severity: 'low'
      };
    }
    if (actual < 20) {
      return {
        cause: 'Camera may not be recording',
        detail: 'VMS shows very low coverage (' + actual + '%) but RA reports ' + reported + '% confidence. The camera may have recently stopped recording, or the recording schedule changed.',
        action: 'Check this camera in your VMS. Verify recording is enabled and the schedule is correct. If the camera was recently changed, RA will recalibrate on the next check cycle.',
        severity: 'high'
      };
    }
    return {
      cause: 'RA score lagging behind a recent change',
      detail: 'RA\'s confidence (' + reported + '%) is higher than current VMS coverage (' + actual + '%). This usually means coverage dropped recently and RA hasn\'t fully adjusted yet.',
      action: 'Run the accuracy audit again in 15-30 minutes. If the deviation persists, click into this camera\'s RA detail page and run a deep verify to investigate.',
      severity: 'medium'
    };
  }

  if (reported < actual) {
    return {
      cause: 'RA is being conservative',
      detail: 'VMS shows ' + actual + '% coverage but RA only reports ' + reported + '% confidence. RA may be penalizing this camera for past gaps or intermittent thumbnail failures.',
      action: 'This is the safe direction -- RA is cautious rather than overconfident. Check the camera\'s RA detail page to see if there are historical gaps or check failures pulling the score down.',
      severity: 'low'
    };
  }

  return null;
}

const SEVERITY_COLORS = {
  high: { bg: 'rgba(239, 68, 68, 0.08)', border: 'rgba(239, 68, 68, 0.2)', icon: '#ef4444' },
  medium: { bg: 'rgba(245, 158, 11, 0.08)', border: 'rgba(245, 158, 11, 0.2)', icon: '#f59e0b' },
  low: { bg: 'rgba(100, 116, 139, 0.06)', border: 'rgba(100, 116, 139, 0.15)', icon: '#64748b' },
};

/* ── Progress Bar Component ── */
const ProgressBar = ({ running, cameraCount, label, startedAt }) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!running || !startedAt) { setElapsed(0); return; }
    const iv = setInterval(() => setElapsed(Date.now() - startedAt), 200);
    return () => clearInterval(iv);
  }, [running, startedAt]);

  if (!running) return null;

  const perCameraMs = 1100;
  const estimatedTotalMs = cameraCount * perCameraMs;
  const estimatedCurrent = Math.min(cameraCount, Math.floor(elapsed / perCameraMs));
  const pct = Math.min(95, (elapsed / estimatedTotalMs) * 100);
  const remainingSec = Math.max(0, Math.ceil((estimatedTotalMs - elapsed) / 1000));

  return (
    <div style={{
      background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
      borderRadius: '12px', padding: '20px', marginBottom: '20px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Loader2 size={18} className="spin" style={{ color: '#00d4aa' }} />
          <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>{label}</span>
        </div>
        <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
          {pct >= 94 ? 'Finalizing results...' : `~${remainingSec}s remaining`}
        </span>
      </div>
      <div style={{
        height: '8px', background: 'rgba(255,255,255,0.06)', borderRadius: '4px',
        overflow: 'hidden', marginBottom: '10px'
      }}>
        <div style={{
          height: '100%', background: 'linear-gradient(90deg, #00d4aa, #00b894)',
          borderRadius: '4px', width: pct + '%',
          transition: 'width 0.3s ease'
        }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-muted)' }}>
        <span>{pct >= 94 ? 'Processing and comparing results...' : `Checking camera ${estimatedCurrent} of ${cameraCount}`}</span>
        <span>{Math.round(pct)}%</span>
      </div>
    </div>
  );
};

/* ── Jitter overlapping scatter points ── */
function jitterScatterData(points) {
  const validPoints = points.filter(p => p.actualCoverage !== null);
  const buckets = new Map();

  for (const p of validPoints) {
    const key = `${Math.round(p.actualCoverage / 2) * 2}-${Math.round(p.raConfidence / 2) * 2}`;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(p);
  }

  const result = [];
  for (const [, group] of buckets) {
    if (group.length === 1) {
      result.push({ ...group[0], displayActual: group[0].actualCoverage, displayRA: group[0].raConfidence, stackCount: 1 });
    } else {
      const cx = group[0].actualCoverage;
      const cy = group[0].raConfidence;
      const radius = Math.min(3, 1 + group.length * 0.3);
      group.forEach((p, i) => {
        const angle = (2 * Math.PI * i) / group.length;
        result.push({
          ...p,
          displayActual: Math.max(0, Math.min(100, cx + radius * Math.cos(angle))),
          displayRA: Math.max(0, Math.min(100, cy + radius * Math.sin(angle))),
          stackCount: group.length
        });
      });
    }
  }
  return result;
}

/* ── Main Component ── */
const RecordingAssuranceAccuracy = () => {
  const navigate = useNavigate();
  const [auditResult, setAuditResult] = useState(() => loadResult(STORAGE_KEYS.audit));
  const [calibrationResult, setCalibrationResult] = useState(() => loadResult(STORAGE_KEYS.calibration));
  const [historyResult, setHistoryResult] = useState(() => loadResult(STORAGE_KEYS.history));
  const [runningAudit, setRunningAudit] = useState(false);
  const [runningCalibration, setRunningCalibration] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [activeTab, setActiveTab] = useState(() => loadResult(STORAGE_KEYS.tab) || 'audit');
  const [cameraCount, setCameraCount] = useState(66);
  const [startedAt, setStartedAt] = useState(null);
  const [expandedCamera, setExpandedCamera] = useState(null);

  useEffect(() => {
    fetchWithTenant('/api/recording-assurance/cameras')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && Array.isArray(data)) setCameraCount(data.length);
        else if (data?.cameras) setCameraCount(data.cameras.length);
      })
      .catch(() => {});
  }, []);

  const switchTab = useCallback((tab) => {
    setActiveTab(tab);
    saveResult(STORAGE_KEYS.tab, tab);
  }, []);

  const runAudit = useCallback(async () => {
    setRunningAudit(true);
    setStartedAt(Date.now());
    switchTab('audit');
    try {
      const res = await fetchWithTenant('/api/recording-assurance/audit/full', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        const data = await res.json();
        setAuditResult(data);
        saveResult(STORAGE_KEYS.audit, data);
        toast.success('Accuracy audit complete');
      } else {
        toast.error('Audit failed');
      }
    } catch (err) {
      toast.error('Audit error: ' + err.message);
    }
    setRunningAudit(false);
    setStartedAt(null);
  }, [switchTab]);

  const runCalibration = useCallback(async () => {
    setRunningCalibration(true);
    setStartedAt(Date.now());
    switchTab('calibration');
    try {
      const res = await fetchWithTenant('/api/recording-assurance/audit/calibration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        const data = await res.json();
        setCalibrationResult(data);
        saveResult(STORAGE_KEYS.calibration, data);
        toast.success('Calibration complete');
      } else {
        toast.error('Calibration failed');
      }
    } catch (err) {
      toast.error('Calibration error: ' + err.message);
    }
    setRunningCalibration(false);
    setStartedAt(null);
  }, [switchTab]);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    switchTab('history');
    try {
      const res = await fetchWithTenant('/api/recording-assurance/audit/history?days=7');
      if (res.ok) {
        const data = await res.json();
        setHistoryResult(data);
        saveResult(STORAGE_KEYS.history, data);
      }
    } catch (err) {
      console.error('History error:', err);
    }
    setLoadingHistory(false);
  }, [switchTab]);

  const isRunning = runningAudit || runningCalibration;
  const hasAnyResult = auditResult || calibrationResult || historyResult;

  const jitteredCalibrationData = calibrationResult
    ? jitterScatterData(calibrationResult.points)
    : [];

  const uncalibratedCameras = calibrationResult
    ? calibrationResult.points
        .filter(p => p.actualCoverage !== null && !p.isCalibrated)
        .sort((a, b) => (b.deviation || 0) - (a.deviation || 0))
    : [];

  const auditEstimateSec = Math.ceil(cameraCount * 1.1);
  const calibEstimateSec = Math.ceil(cameraCount * 0.7);

  return (
    <div className="ra-page" style={{ maxWidth: '100%' }}>
      <RaLicenseBanner />
      {/* Header */}
      <div className="ra-header">
        <div className="ra-header-left">
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/recording-assurance')}>
            <ArrowLeft size={14} /> Dashboard
          </button>
          <Target size={24} className="ra-header-icon" />
          <div>
            <h1>Accuracy validation</h1>
            <p className="ra-subtitle">Prove that Recording Assurance is telling the truth</p>
          </div>
        </div>
      </div>

      {/* Explainer */}
      <div className="ra-card" style={{ marginBottom: '20px', display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
        <Shield size={20} style={{ color: '#00d4aa', flexShrink: 0, marginTop: '2px' }} />
        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          <strong style={{ color: 'var(--text-primary)' }}>How this works:</strong> The accuracy audit independently queries your VMS for actual recording data and compares it against what Recording Assurance reported. For every camera, it checks: does the VMS actually have footage where RA said it would? Is footage missing where RA said it should be? The result is a measurable accuracy score you can verify yourself.
        </div>
      </div>

      {/* ── Action Buttons ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '24px' }}>
        <button onClick={runAudit} disabled={isRunning} style={{
          background: activeTab === 'audit' ? 'rgba(0,212,170,0.08)' : 'var(--bg-secondary)',
          border: activeTab === 'audit' ? '2px solid rgba(0,212,170,0.4)' : '2px solid var(--border-color)',
          borderRadius: '14px', padding: '20px', cursor: isRunning ? 'not-allowed' : 'pointer',
          textAlign: 'left', transition: 'all 0.2s', opacity: isRunning && !runningAudit ? 0.5 : 1
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg, #00d4aa, #00b894)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ClipboardCheck size={18} style={{ color: '#0a0f1a' }} />
            </div>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)' }}>Accuracy audit</div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>~{auditEstimateSec}s for {cameraCount} cameras</div>
            </div>
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '12px' }}>
            Cross-checks every camera against live VMS data to verify RA's accuracy.
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: '#00d4aa', background: 'rgba(0,212,170,0.1)', padding: '6px 14px', borderRadius: '8px' }}>
            {runningAudit ? <><Loader2 size={14} className="spin" /> Running...</> : <><Play size={14} /> Run audit</>}
          </div>
          {auditResult && !runningAudit && (
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '8px' }}>
              Last run: {auditResult.auditedAt ? new Date(auditResult.auditedAt).toLocaleString() : 'N/A'}
            </div>
          )}
        </button>

        <button onClick={runCalibration} disabled={isRunning} style={{
          background: activeTab === 'calibration' ? 'rgba(99,102,241,0.08)' : 'var(--bg-secondary)',
          border: activeTab === 'calibration' ? '2px solid rgba(99,102,241,0.4)' : '2px solid var(--border-color)',
          borderRadius: '14px', padding: '20px', cursor: isRunning ? 'not-allowed' : 'pointer',
          textAlign: 'left', transition: 'all 0.2s', opacity: isRunning && !runningCalibration ? 0.5 : 1
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg, #6366f1, #4f46e5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Crosshair size={18} style={{ color: '#fff' }} />
            </div>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)' }}>Score calibration</div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>~{calibEstimateSec}s for {cameraCount} cameras</div>
            </div>
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '12px' }}>
            Compares RA confidence scores against actual VMS coverage to verify calibration.
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: '#818cf8', background: 'rgba(99,102,241,0.1)', padding: '6px 14px', borderRadius: '8px' }}>
            {runningCalibration ? <><Loader2 size={14} className="spin" /> Running...</> : <><Play size={14} /> Run calibration</>}
          </div>
          {calibrationResult && !runningCalibration && (
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '8px' }}>
              Last run: {calibrationResult.generatedAt ? new Date(calibrationResult.generatedAt).toLocaleString() : 'N/A'}
            </div>
          )}
        </button>

        <button onClick={loadHistory} disabled={isRunning} style={{
          background: activeTab === 'history' ? 'rgba(245,158,11,0.08)' : 'var(--bg-secondary)',
          border: activeTab === 'history' ? '2px solid rgba(245,158,11,0.4)' : '2px solid var(--border-color)',
          borderRadius: '14px', padding: '20px', cursor: isRunning ? 'not-allowed' : 'pointer',
          textAlign: 'left', transition: 'all 0.2s', opacity: isRunning ? 0.5 : 1
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Activity size={18} style={{ color: '#0a0f1a' }} />
            </div>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)' }}>Trend history</div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>7-day accuracy trends</div>
            </div>
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '12px' }}>
            Shows daily pass/fail rates and confidence trends over the past week.
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: '#fbbf24', background: 'rgba(245,158,11,0.1)', padding: '6px 14px', borderRadius: '8px' }}>
            {loadingHistory ? <><Loader2 size={14} className="spin" /> Loading...</> : <><Activity size={14} /> View trends</>}
          </div>
          {historyResult && !loadingHistory && (
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '8px' }}>
              Last loaded: {new Date().toLocaleString()}
            </div>
          )}
        </button>
      </div>

      {/* ── Progress Indicator ── */}
      {runningAudit && (
        <ProgressBar running={true} cameraCount={cameraCount}
          label="Running accuracy audit -- cross-checking every camera against live VMS data"
          startedAt={startedAt} />
      )}
      {runningCalibration && (
        <ProgressBar running={true} cameraCount={cameraCount}
          label="Running score calibration -- comparing RA scores to actual VMS coverage"
          startedAt={startedAt} />
      )}

      {/* ============================================ */}
      {/* AUDIT RESULTS */}
      {/* ============================================ */}
      {activeTab === 'audit' && auditResult && !runningAudit && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
            <div className="ra-card" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '36px', fontWeight: 700, color: auditResult.summary.accuracy && parseFloat(auditResult.summary.accuracy) >= 95 ? '#22c55e' : parseFloat(auditResult.summary.accuracy) >= 80 ? '#f59e0b' : '#ef4444' }}>
                {auditResult.summary.accuracy}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>Overall accuracy</div>
            </div>
            <div className="ra-card" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '36px', fontWeight: 700, color: auditResult.summary.targets.falsePositiveMet ? '#22c55e' : '#ef4444' }}>
                {auditResult.summary.falsePositiveRate}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>
                False alarms <Tooltip text="Cameras that RA flagged as having issues, but the VMS actually has footage. Target: less than 1%." />
              </div>
            </div>
            <div className="ra-card" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '36px', fontWeight: 700, color: auditResult.summary.targets.falseNegativeMet ? '#22c55e' : '#ef4444' }}>
                {auditResult.summary.falseNegativeRate}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>
                Missed failures <Tooltip text="Cameras where RA said everything was fine but the VMS was actually missing footage. Target: 0%." />
              </div>
            </div>
            <div className="ra-card" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '36px', fontWeight: 700, color: 'var(--text-primary)' }}>
                {auditResult.summary.totalCameras}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>Cameras checked</div>
            </div>
          </div>

          <div className="ra-card" style={{ marginBottom: '20px' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: '15px', color: 'var(--text-primary)' }}>Audit breakdown</h3>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{auditResult.auditedAt && new Date(auditResult.auditedAt).toLocaleString()}</span>
            </div>
            <div style={{ padding: '16px 20px' }}>
              <div style={{ display: 'flex', height: '28px', borderRadius: '8px', overflow: 'hidden', marginBottom: '12px' }}>
                {auditResult.summary.breakdown.accuratePositives > 0 && (
                  <div style={{ flex: auditResult.summary.breakdown.accuratePositives, background: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#fff', fontWeight: 600 }}>{auditResult.summary.breakdown.accuratePositives}</div>
                )}
                {auditResult.summary.breakdown.accurateNegatives > 0 && (
                  <div style={{ flex: auditResult.summary.breakdown.accurateNegatives, background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#fff', fontWeight: 600 }}>{auditResult.summary.breakdown.accurateNegatives}</div>
                )}
                {auditResult.summary.breakdown.inconclusive > 0 && (
                  <div style={{ flex: auditResult.summary.breakdown.inconclusive, background: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#fff', fontWeight: 600 }}>{auditResult.summary.breakdown.inconclusive}</div>
                )}
                {auditResult.summary.breakdown.falsePositives > 0 && (
                  <div style={{ flex: auditResult.summary.breakdown.falsePositives, background: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#fff', fontWeight: 600 }}>{auditResult.summary.breakdown.falsePositives}</div>
                )}
                {auditResult.summary.breakdown.falseNegatives > 0 && (
                  <div style={{ flex: auditResult.summary.breakdown.falseNegatives, background: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#fff', fontWeight: 600 }}>{auditResult.summary.breakdown.falseNegatives}</div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '20px', fontSize: '12px', color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#22c55e', marginRight: 6, verticalAlign: 'middle' }} />RA is correct: {auditResult.summary.breakdown.accuratePositives + auditResult.summary.breakdown.accurateNegatives}</span>
                <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#64748b', marginRight: 6, verticalAlign: 'middle' }} />Expected behavior: {auditResult.summary.breakdown.inconclusive}</span>
                {auditResult.summary.breakdown.falsePositives > 0 && <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#ef4444', marginRight: 6, verticalAlign: 'middle' }} />False alarms: {auditResult.summary.breakdown.falsePositives}</span>}
                {auditResult.summary.breakdown.falseNegatives > 0 && <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#dc2626', marginRight: 6, verticalAlign: 'middle' }} />Missed: {auditResult.summary.breakdown.falseNegatives}</span>}
              </div>
            </div>
          </div>

          <div className="ra-card" style={{ marginBottom: '20px' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-color)' }}>
              <h3 style={{ margin: 0, fontSize: '15px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Eye size={16} /> Camera-by-camera comparison
                <Tooltip text="For each camera, this shows what your VMS reports versus what Recording Assurance independently verified. Green = RA got it right. Red = RA got it wrong." />
              </h3>
            </div>
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {(auditResult.cameras || []).map((cam, i) => {
                const v = VERDICT_CONFIG[cam.verdict] || VERDICT_CONFIG.INCONCLUSIVE;
                const VIcon = v.icon;
                const isIssue = cam.verdict === 'FALSE_POSITIVE' || cam.verdict === 'FALSE_NEGATIVE';
                return (
                  <div key={i} style={{
                    display: 'grid', gridTemplateColumns: '200px 1fr 1fr 180px', gap: '12px',
                    padding: '12px 20px', alignItems: 'center',
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                    background: isIssue ? v.bg : 'transparent'
                  }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>{cam.cameraName}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{cam.recordingMode}</div>
                    </div>
                    <div style={{ background: 'var(--bg-primary)', borderRadius: '8px', padding: '10px 14px' }}>
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Your VMS reports</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Server size={14} style={{ color: 'var(--text-muted)' }} />
                        <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
                          {cam.actualCoverage !== undefined ? cam.actualCoverage + '% recorded' : 'Unable to check'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                        <Image size={14} style={{ color: 'var(--text-muted)' }} />
                        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                          Archive thumbnail: {cam.thumbnailExists ? 'found' : 'not found'}
                        </span>
                      </div>
                    </div>
                    <div style={{ background: 'var(--bg-primary)', borderRadius: '8px', padding: '10px 14px' }}>
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Recording Assurance says</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Shield size={14} style={{ color: cam.raStatus === 'VERIFIED' ? '#22c55e' : cam.raStatus === 'FAILED' ? '#ef4444' : '#64748b' }} />
                        <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
                          {cam.raStatus} at {cam.raConfidence}% confidence
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <VIcon size={16} style={{ color: v.color }} />
                      <span style={{ fontSize: '12px', fontWeight: 600, color: v.color }}>{v.label}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* ============================================ */}
      {/* CALIBRATION RESULTS */}
      {/* ============================================ */}
      {activeTab === 'calibration' && calibrationResult && !runningCalibration && (
        <>
          <div className="ra-card" style={{ marginBottom: '20px' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-color)' }}>
              <h3 style={{ margin: 0, fontSize: '15px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Crosshair size={16} /> Score calibration
                <Tooltip text="Each dot is one camera. Cameras on the diagonal line are perfectly calibrated -- RA's score matches what the VMS actually recorded. Green dots are within 15% of actual. Orange dots have a larger gap between what RA reported and what the VMS shows." />
              </h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 260px', gap: '20px', padding: '20px' }}>
              <div>
                <ResponsiveContainer width="100%" height={350}>
                  <ScatterChart margin={{ top: 10, right: 10, bottom: 40, left: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <ReferenceLine
                      segment={[{ x: 0, y: 0 }, { x: 100, y: 100 }]}
                      stroke="rgba(255,255,255,0.15)"
                      strokeDasharray="6 4"
                      strokeWidth={2}
                      label={{ value: 'Perfect calibration', position: 'insideTopLeft', style: { fill: 'rgba(255,255,255,0.25)', fontSize: 11 } }}
                    />
                    <XAxis dataKey="displayActual" name="VMS actual" unit="%" type="number" domain={[0, 100]}
                      label={{ value: 'What the VMS actually recorded', position: 'insideBottom', offset: -25, style: { fill: '#64748b', fontSize: 12 } }}
                      tick={{ fill: '#64748b', fontSize: 11 }} />
                    <YAxis dataKey="displayRA" name="RA score" unit="%" type="number" domain={[0, 100]}
                      label={{ value: 'What RA reported', angle: -90, position: 'insideLeft', offset: -25, style: { fill: '#64748b', fontSize: 12 } }}
                      tick={{ fill: '#64748b', fontSize: 11 }} />
                    <RechartsTooltip content={({ active, payload }) => {
                      if (!active || !payload?.[0]) return null;
                      const d = payload[0].payload;
                      return (
                        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '10px 14px', fontSize: 12 }}>
                          <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>{d.cameraName}</div>
                          <div style={{ color: 'var(--text-muted)' }}>VMS actual: {d.actualCoverage}%</div>
                          <div style={{ color: 'var(--text-muted)' }}>RA reported: {d.raConfidence}%</div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>{d.recordingMode}</div>
                          {d.stackCount > 1 && <div style={{ color: '#00d4aa', fontSize: '11px', marginTop: '2px' }}>{d.stackCount} cameras at this position</div>}
                          {d.baseline && d.baseline.mean !== null && (
                            <div style={{ color: '#818cf8', fontSize: '11px', marginTop: '2px' }}>
                              Baseline for this time: {d.baseline.mean}% (range: {d.baseline.expectedRange?.low}-{d.baseline.expectedRange?.high}%)
                            </div>
                          )}
                          {d.calibrationMethod === 'insufficient_baseline' && (
                            <div style={{ color: '#64748b', fontSize: '11px', marginTop: '2px' }}>Baseline still learning</div>
                          )}
                          <div style={{ color: d.isCalibrated ? '#22c55e' : '#f59e0b', marginTop: '4px', fontWeight: 600 }}>
                            {d.calibrationMethod === 'baseline'
                              ? 'vs baseline: ' + d.deviation + '% ' + (d.isCalibrated ? '- Within normal range' : '- Outside expected range')
                              : 'Deviation: ' + d.deviation + '% ' + (d.isCalibrated ? '- Well calibrated' : '- Needs attention')
                            }
                          </div>
                        </div>
                      );
                    }} />
                    <Scatter data={jitteredCalibrationData}>
                      {jitteredCalibrationData.map((p, i) => (
                        <Cell key={i} fill={p.isCalibrated ? '#22c55e' : '#f59e0b'} fillOpacity={0.85} r={5} />
                      ))}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#22c55e', marginRight: 6, verticalAlign: 'middle' }} />Within 15% (calibrated)</span>
                  <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#f59e0b', marginRight: 6, verticalAlign: 'middle' }} />Over 15% deviation</span>
                  <span><span style={{ display: 'inline-block', width: 20, height: 0, borderTop: '2px dashed rgba(255,255,255,0.25)', marginRight: 6, verticalAlign: 'middle' }} />Perfect match</span>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ padding: '16px', background: 'var(--bg-primary)', borderRadius: '10px', textAlign: 'center' }}>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--text-primary)' }}>{calibrationResult.metrics.calibrationRate}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>Cameras well-calibrated</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>(within 15% of actual)</div>
                </div>
                <div style={{ padding: '16px', background: 'var(--bg-primary)', borderRadius: '10px', textAlign: 'center' }}>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: calibrationResult.metrics.avgDeviation < 10 ? '#22c55e' : '#f59e0b' }}>
                    {calibrationResult.metrics.avgDeviation}%
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>Average deviation</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>(lower is better)</div>
                </div>
                <div style={{ padding: '16px', background: 'var(--bg-primary)', borderRadius: '10px', textAlign: 'center' }}>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--text-primary)' }}>{calibrationResult.metrics.validSamples}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>Cameras tested</div>
                </div>
                <div style={{ padding: '12px', background: 'rgba(0,212,170,0.06)', border: '1px solid rgba(0,212,170,0.15)', borderRadius: '10px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  <strong style={{ color: '#00d4aa' }}>Reading this chart:</strong> Dots on the dashed diagonal line mean RA's confidence score exactly matches actual VMS coverage. Dots above the line mean RA is overconfident. Dots below mean RA is being conservative. Green dots are within an acceptable 15% range.
                </div>
              </div>
            </div>
          </div>

          {/* ── Cameras needing attention ── */}
          {uncalibratedCameras.length > 0 && (
            <div className="ra-card" style={{ marginBottom: '20px' }}>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-color)' }}>
                <h3 style={{ margin: 0, fontSize: '15px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <AlertTriangle size={16} style={{ color: '#f59e0b' }} />
                  Cameras with high deviation ({uncalibratedCameras.length})
                  <Tooltip text="These cameras have a gap of more than 15% between what RA reported and what the VMS actually shows. Click any camera for a root cause analysis and recommended action." />
                </h3>
              </div>
              <div style={{ padding: '0' }}>
                {uncalibratedCameras.map((cam, i) => {
                  const analysis = getDeviationAnalysis(cam);
                  const isExpanded = expandedCamera === cam.cameraId;
                  const severity = analysis ? SEVERITY_COLORS[analysis.severity] : SEVERITY_COLORS.low;

                  return (
                    <div key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <div
                        onClick={() => setExpandedCamera(isExpanded ? null : cam.cameraId)}
                        style={{
                          display: 'grid', gridTemplateColumns: '1fr 120px 120px 120px 36px',
                          gap: '12px', padding: '14px 20px', alignItems: 'center', cursor: 'pointer',
                          background: isExpanded ? 'rgba(245,158,11,0.04)' : 'transparent',
                          transition: 'background 0.15s'
                        }}
                      >
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>{cam.cameraName}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{cam.recordingMode}</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{cam.actualCoverage}%</div>
                          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>VMS actual</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{cam.raConfidence}%</div>
                          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>RA reported</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '13px', fontWeight: 600, color: '#f59e0b' }}>{cam.deviation}%</div>
                          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Deviation</div>
                        </div>
                        <ChevronRight size={16} style={{
                          color: 'var(--text-muted)',
                          transform: isExpanded ? 'rotate(90deg)' : 'none',
                          transition: 'transform 0.2s'
                        }} />
                      </div>
                      {isExpanded && analysis && (
                        <div style={{ padding: '0 20px 16px 20px', background: severity.bg }}>
                          <div style={{
                            border: `1px solid ${severity.border}`,
                            borderRadius: '10px', padding: '16px', marginTop: '4px'
                          }}>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                              <AlertTriangle size={18} style={{ color: severity.icon, flexShrink: 0, marginTop: '2px' }} />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '6px' }}>
                                  {analysis.cause}
                                </div>
                                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '12px' }}>
                                  {analysis.detail}
                                </div>
                                <div style={{
                                  background: 'rgba(0,212,170,0.06)', border: '1px solid rgba(0,212,170,0.15)',
                                  borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5
                                }}>
                                  <strong style={{ color: '#00d4aa' }}>Recommended action:</strong> {analysis.action}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* ============================================ */}
      {/* HISTORY */}
      {/* ============================================ */}
      {activeTab === 'history' && historyResult && !loadingHistory && (
        <div className="ra-card">
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-color)' }}>
            <h3 style={{ margin: 0, fontSize: '15px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Activity size={16} /> Daily accuracy trends
              <Tooltip text="Shows the daily pass rate, fail rate, and error rate across all checks. A healthy system has a consistently high pass rate. Rising fail rates could indicate VMS issues or scoring problems." />
            </h3>
          </div>
          <div style={{ padding: '20px' }}>
            {historyResult.history.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                <Activity size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
                <p>No historical data yet. Check back after a few days of data collection.</p>
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={historyResult.history} margin={{ top: 10, right: 10, bottom: 20, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#64748b', fontSize: 11 }} unit="%" domain={[0, 100]} />
                    <RechartsTooltip contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 12 }} />
                    <Legend />
                    <Bar dataKey="passRate" name="Checks passed" fill="#22c55e" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="failRate" name="Checks failed" fill="#ef4444" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="errorRate" name="Check errors" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginTop: '16px' }}>
                  {historyResult.history.map((day, i) => (
                    <div key={i} style={{ background: 'var(--bg-primary)', borderRadius: '8px', padding: '10px 12px', fontSize: '12px' }}>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>{day.date}</div>
                      <div style={{ color: 'var(--text-muted)' }}>{day.totalChecks} checks</div>
                      <div style={{ color: '#22c55e' }}>Avg confidence: {day.avgConfidence}%</div>
                      {day.gapsDetected > 0 && <div style={{ color: '#f59e0b' }}>{day.gapsDetected} gaps found</div>}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!hasAnyResult && !isRunning && (
        <div className="ra-card" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Target size={48} style={{ color: 'var(--text-muted)', opacity: 0.3, marginBottom: '16px' }} />
          <h3 style={{ color: 'var(--text-primary)', fontSize: '18px', margin: '0 0 8px' }}>Ready to validate</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', margin: 0, maxWidth: '500px', marginLeft: 'auto', marginRight: 'auto' }}>
            Use one of the tools above to verify Recording Assurance is reporting accurately. The accuracy audit is the best place to start.
          </p>
        </div>
      )}
    </div>
  );
};

export default RecordingAssuranceAccuracy;
